/**
 * Scheduling helpers: only future date/times for meetings, interviews, mock interviews.
 */

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** `min` for `<input type="datetime-local" />` in the user's local timezone. */
export function toDateTimeLocalMin(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** `min` for `<input type="date" />` in the user's local calendar. */
export function getTodayDateInputMin() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * `datetime-local` value (YYYY-MM-DDTHH:mm) is interpreted as local wall time.
 */
export function isDateTimeLocalInFuture(scheduledAt) {
  if (!scheduledAt || String(scheduledAt).length < 16) return false;
  const t = new Date(scheduledAt);
  if (Number.isNaN(t.getTime())) return false;
  return t.getTime() > Date.now();
}

/**
 * `datetime-local` value (YYYY-MM-DDTHH:mm) must be at least N hours ahead of now.
 * Interprets the value as local wall time (same as the browser input).
 */
export function isDateTimeLocalAtLeastHoursInFuture(scheduledAt, hours = 3) {
  if (!scheduledAt || String(scheduledAt).length < 16) return false;
  const t = new Date(scheduledAt);
  if (Number.isNaN(t.getTime())) return false;
  const h = Number(hours);
  const minMs = (Number.isFinite(h) ? h : 0) * 60 * 60 * 1000;
  return t.getTime() >= (Date.now() + minMs);
}

/**
 * Convert wall-clock date + time in an IANA timezone to UTC epoch ms.
 * Returns null if no matching instant (invalid local time) or invalid input.
 */
export function zonedWallClockToUtcMillis(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) return null;
  const parts = String(dateStr).slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const y = +parts[0];
  const mo = +parts[1];
  const da = +parts[2];
  if (!y || !mo || !da) return null;
  const tm = String(timeStr).length >= 5 ? String(timeStr).slice(0, 5) : '00:00';
  const [h, mi] = tm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(mi)) return null;

  const tz = timeZone || 'Asia/Colombo';
  const center = Date.UTC(y, mo - 1, da, 12, 0, 0);
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  for (let deltaMin = -96 * 60; deltaMin <= 96 * 60; deltaMin++) {
    const ms = center + deltaMin * 60 * 1000;
    const fp = fmt.formatToParts(new Date(ms));
    const get = (type) => {
      const p = fp.find((x) => x.type === type);
      return p ? +p.value : NaN;
    };
    const py = get('year');
    const pm = get('month');
    const pd = get('day');
    const ph = get('hour');
    const pmi = get('minute');
    if (py === y && pm === mo && pd === da && ph === h && pmi === mi) return ms;
  }
  return null;
}

export function isMeetingStartInFuture(meetingDate, meetingTime, timeZone) {
  const ms = zonedWallClockToUtcMillis(meetingDate, meetingTime, timeZone);
  if (ms == null) return false;
  return ms > Date.now();
}

/**
 * Format a stored datetime into `YYYY-MM-DDTHH:mm` as a wall-clock in the given IANA timezone.
 * This avoids `toISOString()` shifting (which breaks slot mapping).
 */
export function toDateTimeLocalValueInTimeZone(dateTimeStr, timeZone) {
  if (!dateTimeStr) return '';
  const d = new Date(dateTimeStr);
  if (Number.isNaN(d.getTime())) return '';
  const tz = timeZone || 'Asia/Colombo';
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const parts = fmt.formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const y = get('year');
  const mo = get('month');
  const da = get('day');
  const h = get('hour');
  const mi = get('minute');
  if (!y || !mo || !da || !h || !mi) return '';
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

/**
 * Normalise a stored DB datetime (e.g. "2026-04-10 09:00:00" or "2026-04-10T09:00")
 * into the `YYYY-MM-DDTHH:mm` format expected by `<input type="datetime-local" />`.
 *
 * The stored value is treated as wall-clock time in the associated timezone column —
 * NO timezone conversion is performed.  This is the correct behaviour because the DB
 * stores the user-selected wall-clock, not a UTC instant.
 */
export function storedDatetimeToLocalInput(dateTimeStr) {
  if (!dateTimeStr) return '';
  const s = String(dateTimeStr).trim();
  const d = s.slice(0, 10);
  const t = (s.length >= 16 ? s.slice(11, 16) : '').replace(/[^0-9:]/g, '');
  if (d.length !== 10 || t.length < 4) return '';
  return `${d}T${t.slice(0, 5)}`;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Render a stored wall-clock datetime string (DB DATETIME) EXACTLY as it was
 * saved — no browser-timezone conversion, no server-timezone conversion.
 *
 * This is the single display helper to use for `scheduled_at`, `meeting_date +
 * meeting_time`, and any other DATETIME column that stores the user-selected
 * wall-clock.  The associated `timezone` column tells the viewer which TZ the
 * wall-clock belongs to — it is expected to be rendered next to this value.
 *
 * Example: input "2026-04-22 10:00:00" → "22 Apr 2026, 10:00".
 *
 * @param {string|null|undefined} dateTimeStr Stored DATETIME, e.g. "2026-04-22 10:00:00"
 *                                            or "2026-04-22T10:00".
 * @param {Object} [options]
 * @param {boolean} [options.withSeconds=false]
 * @returns {string}
 */
export function formatStoredDateTime(dateTimeStr, options = {}) {
  if (!dateTimeStr) return '-';
  const s = String(dateTimeStr).trim();
  const datePart = s.slice(0, 10);
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return '-';
  const monthName = MONTH_SHORT[parseInt(m, 10) - 1] || m;
  const dayNum = parseInt(d, 10);
  const timePart = s.length >= 16 ? s.slice(11, options.withSeconds ? 19 : 16) : '';
  return `${dayNum} ${monthName} ${y}${timePart ? ', ' + timePart : ''}`;
}

/**
 * Render a stored DATE (YYYY-MM-DD) without browser-TZ drift.
 * Example: "2026-04-22" → "22 Apr 2026".
 */
export function formatStoredDate(dateStr) {
  if (!dateStr) return '-';
  const datePart = String(dateStr).trim().slice(0, 10);
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return '-';
  const monthName = MONTH_SHORT[parseInt(m, 10) - 1] || m;
  return `${parseInt(d, 10)} ${monthName} ${y}`;
}

/**
 * Render a stored TIME (HH:mm:ss or HH:mm) as HH:mm.
 */
export function formatStoredTime(timeStr) {
  if (!timeStr) return '';
  const s = String(timeStr).trim();
  return s.slice(0, 5);
}
